<template>
  <div class="upcoming-emails-list-content">
    <LoadingSpinner v-if="loading" position="fixed" :message="loadingMessage" />
    
    <DetailEmpty v-else-if="emails.length === 0" tag="p" class="no-emails" :text="emptyMessage" />
    
    <div v-else class="emails-list">
      <div 
        v-for="email in emails" 
        :key="email.id" 
        class="email-item upcoming"
      >
        <div class="email-item-header">
          <div class="email-item-info">
            <span class="email-item-date">
              <i class="fas fa-calendar-alt"></i>
              Programmé pour le {{ formatDate(email.scheduled_date) }}
            </span>
            <span class="badge badge-upcoming">{{ email.email_type }}</span>
          </div>
        </div>
        <div class="email-item-content">
          <div v-if="email.quote_human_id && showQuoteLink">
            <strong>Demande associée :</strong> 
            <router-link :to="`/quotes/${email.quote_id}`" class="quote-link">
              #{{ email.quote_human_id }}
            </router-link>
            <span v-if="email.quote_formation_nom"> - {{ email.quote_formation_nom }}</span>
          </div>
          <div v-if="email.assigned_user" class="assigned-user">
            <i class="fas fa-user"></i>
            Commercial assigné : {{ email.assigned_user }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import DetailEmpty from '../data/DetailEmpty.vue'
import LoadingSpinner from '../ui/LoadingSpinner.vue'
import { formatDate as formatDateUtil } from '../../utils/dateFormatters.js'

const props = defineProps({
  emails: {
    type: Array,
    default: () => []
  },
  loading: {
    type: Boolean,
    default: false
  },
  loadingMessage: {
    type: String,
    default: 'Chargement des mails à venir...'
  },
  emptyMessage: {
    type: String,
    default: 'Aucun mail automatique programmé.'
  },
  showQuoteLink: {
    type: Boolean,
    default: true
  },
  formatDateFn: {
    type: Function,
    default: null
  }
})

// Utiliser la fonction formatDate passée en prop ou celle par défaut
const formatDate = (dateString) => {
  if (props.formatDateFn) {
    return props.formatDateFn(dateString)
  }
  if (!dateString) return 'Non renseigné'
  return formatDateUtil(dateString)
}
</script>

<style scoped>
.upcoming-emails-list-content {
  width: 100%;
}

/* La phrase d'absence de toutes les fiches (`DetailEmpty`) ; seul l'espace
   autour est propre à la liste. */
.no-emails.detail-empty {
  padding: var(--spacing-4) 0;
}

.emails-list {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-md);
}

.email-item {
  background: white;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-sm);
  padding: var(--spacing-md);
  transition: all var(--transition-base);
}

.email-item.upcoming {
  background: linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%);
  border-color: var(--color-warning, #f59e0b);
  border-left: 4px solid var(--color-warning, #f59e0b);
}

.email-item:hover {
  box-shadow: var(--shadow-sm);
}

.email-item-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: var(--spacing-sm);
  flex-wrap: wrap;
  gap: var(--spacing-sm);
}

.email-item-info {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  flex-wrap: wrap;
}

.email-item-date {
  color: var(--color-warning-dark, #d97706);
  font-size: 0.9rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: var(--spacing-xs);
}

.email-item-date i {
  font-size: 0.85rem;
}

.email-item-content {
  color: var(--color-text-primary);
  line-height: 1.6;
  white-space: pre-wrap;
  margin-top: var(--spacing-sm);
}

.badge-upcoming {
  background: var(--color-warning, #f59e0b);
  color: white;
  padding: 2px 8px;
  border-radius: var(--radius-xs);
  font-size: 0.75rem;
  font-weight: 600;
}

.quote-link {
  color: var(--color-primary);
  text-decoration: none;
  font-weight: 500;
}

.quote-link:hover {
  text-decoration: underline;
}

.assigned-user {
  margin-top: var(--spacing-xs);
  color: var(--color-text-secondary);
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  gap: var(--spacing-xs);
}

.assigned-user i {
  color: var(--color-text-tertiary);
}
</style>
